<div class="fertilization-balance-table-container">
  <mat-table [dataSource]="dataSource" class="fertilization-balance-table mat-elevation-z4">
    <!-- Crop Advice Name Column -->
    <ng-container matColumnDef="cropAdviceName">
      <mat-header-cell *matHeaderCellDef>
        {{ 'cropAdviceName' | translate }}
      </mat-header-cell>
      <mat-cell *matCellDef="let setting">
        <div class="crop-info">
          <div class="crop-name">
            {{ setting.name }} ({{ setting.areaInHectares | number: '0.1-2' }} ha)
          </div>
          <div class="crop-type text--label">{{ setting.cropName }}</div>
          <div class="crop-variety text--label">
            {{ setting.cropVarietyName || getWarningLabelTranslation('NoCropVariety') }}
          </div>
          @if (setting.businessRuleViolationTranslationCodes.length > 0) {
            <div class="background-color--alert-50 text-color--alert-500 warning-label">
              {{ getWarningLabelTranslation(setting.businessRuleViolationTranslationCodes[0]) }}
            </div>
          }
        </div>
      </mat-cell>
    </ng-container>

    <!-- Participation Column -->
    <ng-container matColumnDef="participate">
      <mat-header-cell *matHeaderCellDef>
        {{ 'fertilizationBalance.participate' | translate }}
      </mat-header-cell>
      <mat-cell *matCellDef="let setting">
        <mat-slide-toggle
          color="primary"
          [formControl]="getFormControl(setting.adviceId, 'isParticipating')"
          class="flex--justify-left"
        />
      </mat-cell>
    </ng-container>

    <!-- Samples By Column -->
    <ng-container matColumnDef="samplesBy">
      <mat-header-cell *matHeaderCellDef>
        {{ 'fertilizationBalance.samplesBy' | translate }}
      </mat-header-cell>
      <mat-cell *matCellDef="let setting">
        <mat-form-field class="table-form-field">
          <mat-select
            [value]="getFormControl(setting.adviceId, 'samplesBy').value"
            [disabled]="true"
            [placeholder]="'samplesBy' | translate"
          >
            @for (samplesBy of samplesByKeys; track samplesBy) {
              <mat-option [value]="samplesBy">
                {{ 'fertilizationBalance.' + (samplesBy | lowercase) | translate }}
              </mat-option>
            }
          </mat-select>
        </mat-form-field>
      </mat-cell>
    </ng-container>

    <!-- Expected Yield Column -->
    <ng-container matColumnDef="expectedCropYieldProduct">
      <mat-header-cell *matHeaderCellDef>
        {{ 'fertilizationBalance.expectedCropYieldProduct' | translate }}
      </mat-header-cell>
      <mat-cell *matCellDef="let setting">
        <mat-form-field class="table-form-field">
          <input
            matInput
            type="number"
            [formControl]="getFormControl(setting.adviceId, 'expectedCropYieldProduct')"
          />
        </mat-form-field>
      </mat-cell>
    </ng-container>

    <!-- Specific referenceblock Column -->
    <ng-container matColumnDef="specificReferenceBlock">
      <mat-header-cell *matHeaderCellDef>
        {{ 'fertilizationBalance.specificReferenceBlock' | translate }}
      </mat-header-cell>
      <mat-cell *matCellDef="let setting">
        <mat-slide-toggle
          color="primary"
          [formControl]="getFormControl(setting.adviceId, 'isSpecificReferenceBlock')"
          [aria-label]="'fertilizationBalance.specificReferenceBlock' | translate"
          class="flex--justify-left"
        />
        @if (
          hasSpecificReferenceBlockData(setting.specificReferenceBlockData) ||
          isSpecificReferenceBlockLocked(setting)
        ) {
          <fieldset
            class="specific-reference-block-container"
            [class.reference-block-readonly]="isSpecificReferenceBlockLocked(setting)"
            [disabled]="isSpecificReferenceBlockLocked(setting)"
            [attr.aria-disabled]="isSpecificReferenceBlockLocked(setting)"
            [attr.aria-label]="'fertilizationBalance.specificReferenceBlock' | translate"
          >
            @if (!isSpecificReferenceBlockLocked(setting)) {
              <div>
                <mat-icon class="edit-icon" (click)="editSpecificReferenceBlock.emit(setting)"
                  >edit</mat-icon
                >
              </div>
            }

            <div class="specific-reference-block-details">
              @if (hasSpecificReferenceBlockData(setting.specificReferenceBlockData)) {
                <div class="specific-reference-block-coordinates">
                  <div>
                    {{ 'fertilizationBalance.latitude' | translate }}:
                    {{ setting.specificReferenceBlockData.latitude }}
                  </div>
                  <div>
                    {{ 'fertilizationBalance.longitude' | translate }}:
                    {{ setting.specificReferenceBlockData.longitude }}
                  </div>
                  @if (!hasGpsCoordinates(setting.specificReferenceBlockData)) {
                    <mat-icon
                      class="gps-warning"
                      [matTooltip]="'fertilizationBalance.gpsMissingWarning' | translate"
                      matTooltipPosition="below"
                      [attr.aria-label]="'fertilizationBalance.gpsMissingWarning' | translate"
                      aria-hidden="false"
                      role="img"
                      tabindex="0"
                      >warning</mat-icon
                    >
                  }
                </div>
                <div
                  class="remark-text"
                  [matTooltip]="setting.specificReferenceBlockData.remarks"
                  matTooltipPosition="below"
                >
                  {{ 'fertilizationBalance.mobileNumber' | translate }}:
                  {{ setting.specificReferenceBlockData.remarks }}
                </div>
              } @else {
                <span>—</span>
              }
            </div>
          </fieldset>
        }
      </mat-cell>
    </ng-container>

    <!-- Participation Mail Date Column -->
    <ng-container matColumnDef="participationMailDate">
      <mat-header-cell *matHeaderCellDef>
        {{ 'fertilizationBalance.participationMailDate' | translate }}
      </mat-header-cell>
      <mat-cell *matCellDef="let setting">
        {{ setting.participationMailDate | date: 'short' }}
      </mat-cell>
    </ng-container>

    <!-- External Order ID Column -->
    <ng-container matColumnDef="externalOrderId">
      <mat-header-cell *matHeaderCellDef>
        {{ 'fertilizationBalance.externalOrderId' | translate }}
      </mat-header-cell>
      <mat-cell *matCellDef="let setting">
        {{ setting.externalOrderId }}
      </mat-cell>
    </ng-container>

    <!-- Order Date Column -->
    <ng-container matColumnDef="orderDate">
      <mat-header-cell *matHeaderCellDef>
        {{ 'fertilizationBalance.orderDate' | translate }}
      </mat-header-cell>
      <mat-cell *matCellDef="let setting">
        {{ setting.orderDate | date: 'short' }}
      </mat-cell>
    </ng-container>

    <!-- Cancel Column -->
    <ng-container matColumnDef="cancel">
      <mat-header-cell *matHeaderCellDef>
        {{ 'fertilizationBalance.cancel' | translate }}
      </mat-header-cell>
      <mat-cell *matCellDef="let setting">
        <mat-slide-toggle
          color="primary"
          [formControl]="getFormControl(setting.adviceId, 'isCanceling')"
          class="flex--justify-left"
        />
      </mat-cell>
    </ng-container>

    <!-- Cancellation Date Column -->
    <ng-container matColumnDef="cancellationDate">
      <mat-header-cell *matHeaderCellDef>
        {{ 'fertilizationBalance.cancellationDate' | translate }}
      </mat-header-cell>
      <mat-cell *matCellDef="let setting">
        {{ setting.cancelationDate | date: 'short' }}
      </mat-cell>
    </ng-container>

    <mat-header-row *matHeaderRowDef="displayedColumns"></mat-header-row>
    <mat-row *matRowDef="let row; columns: displayedColumns"></mat-row>
  </mat-table>
</div>
